import { type Component, createEffect, createMemo, createSignal, For, mergeProps, onCleanup, onMount, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import { useFieldError } from '~/hooks/useFieldError'
import type { SliderColorType, SliderMarkType, SliderPropsType, SliderSizeType } from '~/types/Slider'
import FieldMessage from './FieldMessage'
import { useFormContext } from './Form'

const trackHeights: Record<SliderSizeType, string> = { xs: 'h-1', sm: 'h-1', md: 'h-1.5', lg: 'h-2', xl: 'h-2.5' }
const thumbSizes: Record<SliderSizeType, number> = { xs: 12, sm: 14, md: 16, lg: 20, xl: 24 }

/** Riel recorrido: color sólido del tema. 'default' usa el token inverso (como Button). */
const colorClasses: Record<SliderColorType, string> = {
  primary: 'bg-primary-500',
  secondary: 'bg-slate-500',
  success: 'bg-green-500',
  danger: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
  default: 'bg-inverse'
}

const MAX_AUTO_MARKS = 30

/**
 * Slider de un solo pulgar con riel recorrido, burbuja de valor, marcas y
 * colores/tamaños del tema. Interacción por pointer events (no input range
 * nativo) para que el diseño y la burbuja sean libres. El valor viaja en un
 * input oculto con `name` para integrarse con `<Form>`/`useForm`.
 */
const Slider: Component<SliderPropsType> = props => {
  const merged = mergeProps(
    {
      min: 0,
      max: 100,
      step: 1,
      size: 'md' as SliderSizeType,
      color: 'primary' as SliderColorType,
      disabled: false,
      showValue: false
    },
    props
  )

  const [local] = splitProps(merged, [
    'class',
    'min',
    'max',
    'step',
    'size',
    'color',
    'disabled',
    'showValue',
    'marks',
    'name',
    'error',
    'helperText',
    'value',
    'defaultValue',
    'onChange',
    'onChangeEnd',
    'aria-label'
  ])

  const [value, setValue] = createSignal<number>(local.defaultValue ?? local.min)
  const [dragging, setDragging] = createSignal(false)
  const [hovered, setHovered] = createSignal(false)

  createEffect(() => {
    if (props.value !== undefined) setValue(props.value)
  })

  let trackRef: HTMLDivElement | undefined

  const clamp = (v: number) => Math.min(local.max, Math.max(local.min, v))
  const snap = (v: number) => {
    const s = local.step > 0 ? local.step : 1
    return clamp(Math.round((v - local.min) / s) * s + local.min)
  }
  const pct = (v: number) => ((v - local.min) / (local.max - local.min)) * 100

  const currentValue = () => (local.value !== undefined ? local.value : value())

  const valueFromClientX = (clientX: number): number => {
    if (!trackRef) return local.min
    const rect = trackRef.getBoundingClientRect()
    if (rect.width <= 0) return local.min
    const ratio = (clientX - rect.left) / rect.width
    return snap(local.min + ratio * (local.max - local.min))
  }

  const commit = (next: number, end = false) => {
    setValue(next)
    local.onChange?.(next)
    if (end) local.onChangeEnd?.(next)
  }

  const onPointerDown = (e: PointerEvent) => {
    if (local.disabled) return
    e.preventDefault()
    trackRef?.setPointerCapture(e.pointerId)
    setDragging(true)
    commit(valueFromClientX(e.clientX))
  }

  const onPointerMove = (e: PointerEvent) => {
    if (!dragging()) return
    commit(valueFromClientX(e.clientX))
  }

  const onPointerUp = (e: PointerEvent) => {
    if (!dragging()) return
    setDragging(false)
    commit(valueFromClientX(e.clientX), true)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (local.disabled) return
    const s = local.step > 0 ? local.step : 1
    let next: number | null = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = snap(currentValue() + s)
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = snap(currentValue() - s)
    if (e.key === 'Home') next = local.min
    if (e.key === 'End') next = local.max
    if (next === null) return
    e.preventDefault()
    commit(next, true)
  }

  /** Marcas: array explícito, o una por step cuando `marks` es true. */
  const markItems = createMemo<SliderMarkType[]>(() => {
    if (Array.isArray(local.marks)) return local.marks
    if (local.marks === true) {
      const s = local.step > 0 ? local.step : 1
      const count = Math.round((local.max - local.min) / s) + 1
      if (count > MAX_AUTO_MARKS) return []
      return Array.from({ length: count }, (_, i) => ({ value: snap(local.min + i * s) }))
    }
    return []
  })

  const hasMarkLabels = createMemo(() => markItems().some(m => m.label))

  // Emitir change del input oculto solo cuando el valor cambia de verdad.
  let hiddenRef: HTMLInputElement | undefined
  let lastEmitted: string | null = null
  createEffect(() => {
    if (!local.name || !hiddenRef) return
    const current = String(currentValue())
    if (current === lastEmitted) return
    lastEmitted = current
    // Los dos: el `<Form>` escucha `input` para revalidar en vivo y `change`
    // para el resto. Con sólo uno, la validación se quedaba congelada.
    hiddenRef.dispatchEvent(new Event('input', { bubbles: true }))
    hiddenRef.dispatchEvent(new Event('change', { bubbles: true }))
  })

  /**
   * El valor vive en una señal y el input oculto sólo lo refleja, así que el
   * `<Form>` no puede escribirlo por el DOM: al siguiente render se pierde.
   */
  const formCtx = useFormContext()
  const resolvedError = useFieldError(
    () => local.name,
    () => local.error
  )

  onMount(() => {
    const name = local.name
    if (!name || !formCtx) return

    formCtx.registerField(name, {
      getValue: () => currentValue(),
      setValue: next => {
        const parsed = Number(next)
        if (!Number.isNaN(parsed)) setValue(parsed)
      }
    })

    onCleanup(() => formCtx.unregisterField(name))
  })

  const thumbSize = thumbSizes[local.size]
  const showBubble = () => local.showValue && (dragging() || hovered())

  return (
    // El control es un `role="slider"` con los rieles posicionados dentro, así
    // que el mensaje necesita su propio hueco fuera: dentro se dibujaba encima
    // de la barra.
    <div class="w-full">
      <div
        ref={trackRef}
        role="slider"
        tabIndex={local.disabled ? -1 : 0}
        aria-valuemin={local.min}
        aria-valuemax={local.max}
        aria-valuenow={currentValue()}
        aria-valuetext={String(currentValue())}
        aria-label={local['aria-label']}
        aria-disabled={local.disabled || undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        class={twMerge(
          'relative w-full select-none touch-none rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          local.disabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : 'cursor-pointer',
          hasMarkLabels() ? 'pb-6' : 'pb-2',
          local.class
        )}
        style={{ height: `${thumbSize + 4}px`, 'padding-top': '4px' }}
      >
        {/* Riel base */}
        <div class={twMerge('absolute inset-x-0 top-1/2 -translate-y-1/2 rounded-full bg-surface-muted', trackHeights[local.size])} />

        {/* Riel recorrido */}
        <div class={twMerge('absolute top-1/2 -translate-y-1/2 rounded-full', trackHeights[local.size], colorClasses[local.color])} style={{ left: 0, width: `${pct(currentValue())}%` }} />

        {/* Marcas */}
        <Show when={markItems().length > 0}>
          <For each={markItems()}>
            {m => (
              <div class="absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center" style={{ left: `${pct(m.value)}%` }}>
                <span class="h-3 w-0.5 rounded-full bg-border-strong" />
                <Show when={m.label}>
                  <span class="absolute top-4 whitespace-nowrap text-[10px] text-foreground-subtle select-none">{m.label}</span>
                </Show>
              </div>
            )}
          </For>
        </Show>

        {/* Pulgar + burbuja */}
        <div class="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: `${pct(currentValue())}%` }}>
          <Show when={showBubble()}>
            <div
              class="absolute left-1/2 -translate-x-1/2 rounded-md bg-inverse px-2 py-1 text-[11px] font-semibold tabular-nums text-on-inverse shadow-lg whitespace-nowrap"
              style={{ bottom: `${thumbSize + 8}px` }}
            >
              {currentValue()}
              <span class="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-inverse" />
            </div>
          </Show>
          <div
            class={twMerge('rounded-full border-2 bg-surface shadow-sm transition-transform', colorClasses[local.color], dragging() ? 'scale-110' : '')}
            style={{ width: `${thumbSize}px`, height: `${thumbSize}px` }}
          />
        </div>
      </div>

      <Show when={local.name}>
        <input ref={hiddenRef} type="hidden" name={local.name} value={String(currentValue())} />
      </Show>

      <FieldMessage size={local.size} error={resolvedError()} helperText={local.helperText} indent={false} />
    </div>
  )
}

export default Slider
